export type DatasourceExecutionNodeKind =
  | 'semantic_base'
  | 'working_set'
  | 'aggregate_base'
  | 'window_input_base'
  | 'worksheet_presentation'
  | 'worksheet_interaction_cube'

export const CANONICAL_BASE_CONTRACT_VERSION = 1
export const CANONICAL_HELPER_SIDECAR_CONTRACT_VERSION = 1

export type CanonicalBaseSelectionCacheMode =
  | 'working_set_table'
  | 'base_passthrough'

// Phase 3b: `active_plus_siblings` is a new cross-sheet default that sits between
// `active_only` (shaped caches only visible to the active sheet's execution scope)
// and `full` (shaped caches visible to any sheet on the dataset regardless of
// workbook membership). It declares that shaped caches are shared across sibling
// sheets in the same workbook session once Phase 1-3a invariants are met.
export type CanonicalBaseShapedCacheMode =
  | 'disabled'
  | 'active_only'
  | 'active_plus_siblings'
  | 'full'

export type CanonicalBaseDescriptor = {
  dataset_id?: string | null
  workbook_id?: string | null
  backend_import_id?: string | null
  extract_identity?: string | null
  semantic_spec_hash?: string | null
  base_contract_version: number
  helper_sidecar_contract_version: number
  selection_cache_mode: CanonicalBaseSelectionCacheMode
  shaped_cache_mode: CanonicalBaseShapedCacheMode
}

export type DatasourceExecutionNodeIdentityInput = {
  nodeKind: DatasourceExecutionNodeKind
  datasetId?: string | null
  route?: string | null
  extractIdentity?: string | null
  semanticSpecHash?: string | null
  workingSetSignature?: string | null
  grainSignature?: string | null
  measureSetSignature?: string | null
  calcParamSignature?: string | null
  windowInputSignature?: string | null
  plannerNodeSignature?: string | null
  executionSignature?: string | null
}

export type DatasourceExecutionNodeRequest = {
  nodeKind: DatasourceExecutionNodeKind
  nodeSignature: string
  sql: string
  sourceTableName?: string | null
  preludeStatements?: string[] | null
  // Optional reusable-node bindings for physical prelude stages required to
  // materialize this node. The worker validates the original stage shape before
  // replacing that stage with a durable relation read.
  preludeDatasourceNodes?: DatasourceExecutionNodePlanBinding[] | null
  maxTables?: number | null
  datasetId?: string | null
  route?: string | null
  extractIdentity?: string | null
  semanticSpecHash?: string | null
  workingSetSignature?: string | null
  grainSignature?: string | null
  measureSetSignature?: string | null
  calcParamSignature?: string | null
  windowInputSignature?: string | null
  plannerNodeSignature?: string | null
  executionSignature?: string | null
  bindingGeneration?: string | null
  physicalPlanSignature?: string | null
}

export type DatasourceExecutionNodePlanBinding = DatasourceExecutionNodeRequest

export type DatasourceExecutionNodeManifestEntry = {
  key: string
  node_kind: DatasourceExecutionNodeKind
  table_name: string
  extract_identity: string | null
  semantic_spec_hash: string | null
  working_set_signature: string | null
  grain_signature: string | null
  measure_set_signature: string | null
  calc_param_signature: string | null
  window_input_signature: string | null
  planner_node_signature: string | null
  execution_signature: string | null
  canonical_root_key: string | null
  created_at_ms: number
  last_used_at_ms: number
  row_count: number | null
  bytes_est: number | null
  working_set_kind?: string | null
  reuse_count?: number | null
}

export type DatasourceExecutionNodePlanSignatureHint = {
  materializedSqlHash?: string | null
  materializedTable?: string | null
  aggBaseMaterializedTable?: string | null
}

const hashStringToBase36 = (value: string): string => {
  let h1 = 2166136261
  let h2 = 2166136261 ^ 0x9e3779b9
  const s = String(value ?? '')
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i)
    h1 ^= c
    h1 = Math.imul(h1, 16777619)
    h2 ^= c
    h2 = Math.imul(h2, 16777619)
  }
  const a = (h1 >>> 0).toString(36).padStart(8, '0')
  const b = (h2 >>> 0).toString(36).padStart(8, '0')
  return `${a}${b}`
}

const stableStringify = (value: any): string => {
  const seen = new WeakSet<object>()
  const normalize = (current: any): any => {
    if (current === null || typeof current === 'undefined') return current
    if (typeof current === 'bigint') return current.toString()
    if (typeof current !== 'object') return current
    if (seen.has(current as object)) return '[Circular]'
    seen.add(current as object)
    if (Array.isArray(current)) return current.map(normalize)
    const out: Record<string, any> = {}
    for (const key of Object.keys(current).sort()) out[key] = normalize(current[key])
    return out
  }
  try {
    return JSON.stringify(normalize(value))
  } catch {
    return String(value ?? '')
  }
}

const normalizeText = (value: any): string | null => {
  const text = String(value ?? '').trim()
  return text || null
}

export const buildCanonicalBaseDescriptor = (input: {
  datasetId?: string | null
  workbookId?: string | null
  backendImportId?: string | null
  extractIdentity?: string | null
  semanticSpecHash?: string | null
  selectionCacheMode?: CanonicalBaseSelectionCacheMode | null
  shapedCacheMode?: CanonicalBaseShapedCacheMode | null
  baseContractVersion?: number | null
  helperSidecarContractVersion?: number | null
}): CanonicalBaseDescriptor => ({
  dataset_id: normalizeText(input.datasetId),
  workbook_id: normalizeText(input.workbookId),
  backend_import_id: normalizeText(input.backendImportId),
  extract_identity: normalizeText(input.extractIdentity),
  semantic_spec_hash: normalizeText(input.semanticSpecHash),
  base_contract_version: Math.max(
    1,
    Math.floor(Number(input.baseContractVersion ?? CANONICAL_BASE_CONTRACT_VERSION) || CANONICAL_BASE_CONTRACT_VERSION),
  ),
  helper_sidecar_contract_version: Math.max(
    1,
    Math.floor(Number(input.helperSidecarContractVersion ?? CANONICAL_HELPER_SIDECAR_CONTRACT_VERSION) || CANONICAL_HELPER_SIDECAR_CONTRACT_VERSION),
  ),
  selection_cache_mode:
    input.selectionCacheMode === 'base_passthrough'
      ? 'base_passthrough'
      : 'working_set_table',
  shaped_cache_mode:
    input.shapedCacheMode === 'full'
      ? 'full'
      : input.shapedCacheMode === 'active_plus_siblings'
        ? 'active_plus_siblings'
        : input.shapedCacheMode === 'active_only'
          ? 'active_only'
          : 'disabled',
})

const normalizeNodeKind = (value: any): DatasourceExecutionNodeKind | null => {
  const text = String(value ?? '').trim().toLowerCase()
  switch (text) {
    case 'semantic_base':
    case 'working_set':
    case 'aggregate_base':
    case 'window_input_base':
    case 'worksheet_presentation':
    case 'worksheet_interaction_cube':
      return text
    default:
      return null
  }
}

const tableNamePrefixByKind: Record<DatasourceExecutionNodeKind, string> = {
  semantic_base: '__fg_semn_',
  working_set: '__fg_wsn_',
  aggregate_base: '__fg_aggp_',
  window_input_base: '__fg_winp_',
  worksheet_presentation: '__fg_wsp_',
  worksheet_interaction_cube: '__fg_wic_',
}

export const isDatasourceExecutionNodeTableName = (tableName: string | null | undefined): boolean => {
  const relation = String(tableName ?? '').trim().toLowerCase()
  if (!relation) return false
  return Object.values(tableNamePrefixByKind).some((prefix) => relation.startsWith(prefix))
}

const sqlSignaturePrefixByKind: Record<DatasourceExecutionNodeKind, string> = {
  semantic_base: 'SemanticBaseSql',
  working_set: 'WorkingSetSql',
  aggregate_base: 'AggregateBaseSql',
  window_input_base: 'WindowInputBaseSql',
  worksheet_presentation: 'WorksheetPresentationSql',
  worksheet_interaction_cube: 'WorksheetInteractionCubeSql',
}

export const normalizeDatasourceExecutionPlanTempTableFamily = (raw: any): string | null => {
  const token = String(raw ?? '').trim().toLowerCase()
  if (!token.startsWith('fg_plan_')) return null
  return token
    .replace(/_chunk_\d+(?:__q\d+)?$/i, '')
    .replace(/_window(?:_input(?:_base)?)?$/i, '')
    .replace(/_agg_base(?:_\d+)?$/i, '')
    .replace(/_shared_stage_\d+$/i, '')
    .replace(/_wrapper_stage_\d+$/i, '')
}

export const buildDatasourceExecutionNodePlannerSignature = (
  nodeKind: DatasourceExecutionNodeKind,
  sql: string | null | undefined,
  planHint?: DatasourceExecutionNodePlanSignatureHint | null,
): string | null => {
  const materializedSqlHash = normalizeText(planHint?.materializedSqlHash)
  const materializedTableFamily = normalizeDatasourceExecutionPlanTempTableFamily(
    planHint?.materializedTable,
  )
  const aggBaseMaterializedTableFamily = normalizeDatasourceExecutionPlanTempTableFamily(
    planHint?.aggBaseMaterializedTable,
  )
  if (materializedSqlHash || materializedTableFamily || aggBaseMaterializedTableFamily) {
    return `${sqlSignaturePrefixByKind[nodeKind]}:${hashStringToBase36(stableStringify({
      mode: 'plan_materialization',
      nodeKind,
      materializedSqlHash,
      materializedTableFamily,
      aggBaseMaterializedTableFamily,
    })).slice(0, 24)}`
  }
  return buildDatasourceExecutionNodeSqlSignature(nodeKind, sql)
}

export const normalizeDatasourceExecutionNodeSqlIdentity = (sql: string | null | undefined): string => {
  const text = String(sql ?? '').trim()
  if (!text) return ''
  const planTableMap = new Map<string, string>()
  let planTableIndex = 0
  const datasourceNodeTableMap = new Map<string, string>()
  let datasourceNodeTableIndex = 0
  const normalizePlanTableName = (rawName: string): string => {
    const key = String(rawName ?? '').toLowerCase()
    if (!planTableMap.has(key)) {
      planTableMap.set(key, `__FG_PLAN_TABLE__${planTableIndex++}__`)
    }
    return planTableMap.get(key) || '__FG_PLAN_TABLE__0__'
  }
  const normalizeDatasourceNodeTableName = (rawName: string): string => {
    const key = String(rawName ?? '').toLowerCase()
    if (!datasourceNodeTableMap.has(key)) {
      datasourceNodeTableMap.set(key, `__FG_NODE_TABLE__${datasourceNodeTableIndex++}__`)
    }
    return datasourceNodeTableMap.get(key) || '__FG_NODE_TABLE__0__'
  }
  return text
    .replace(/"main"\."(__fg_(?:semn|wsn|aggp|winp|wsp)_[a-z0-9_]+)"/gi, (_m, tableName) => (
      `"main"."${normalizeDatasourceNodeTableName(tableName)}"`
    ))
    .replace(/\bmain\.(__fg_(?:semn|wsn|aggp|winp|wsp)_[a-z0-9_]+)\b/gi, (_m, tableName) => (
      `main.${normalizeDatasourceNodeTableName(tableName)}`
    ))
    .replace(/"(__fg_(?:semn|wsn|aggp|winp|wsp)_[a-z0-9_]+)"/gi, (_m, tableName) => (
      `"${normalizeDatasourceNodeTableName(tableName)}"`
    ))
    .replace(/\b(__fg_(?:semn|wsn|aggp|winp|wsp)_[a-z0-9_]+)\b/gi, (_m, tableName) => (
      normalizeDatasourceNodeTableName(tableName)
    ))
    .replace(/\b__fg_ft_[a-z0-9_]+_\d+\b/gi, '__FG_FILTER_TABLE__IDX__')
    .replace(/\b__FG_FILTER_TABLE__\d+__\b/g, '__FG_FILTER_TABLE__IDX__')
    .replace(/\/\*__FG_FILTER:\d+(?::([A-Za-z0-9_]+))?\*\//g, (_m, partRaw?: string) => {
      const part = String(partRaw ?? '').trim()
      return part ? `/*__FG_FILTER:IDX:${part}__*/` : '/*__FG_FILTER:IDX__*/'
    })
    .replace(/\bfg_plan_[a-z0-9_]+(?:__q\d+)?\b/gi, (match) => normalizePlanTableName(match))
}

export const buildDatasourceExecutionNodeSqlSignature = (
  nodeKind: DatasourceExecutionNodeKind,
  sql: string | null | undefined,
): string | null => {
  const text = normalizeDatasourceExecutionNodeSqlIdentity(sql)
  if (!text) return null
  return `${sqlSignaturePrefixByKind[nodeKind]}:${hashStringToBase36(text).slice(0, 24)}`
}

const normalizeNodeIdentityDimensions = (
  nodeKind: DatasourceExecutionNodeKind,
  input: DatasourceExecutionNodeIdentityInput,
): {
  workingSetSignature: string | null
  grainSignature: string | null
  measureSetSignature: string | null
  calcParamSignature: string | null
  windowInputSignature: string | null
} => {
  if (nodeKind === 'aggregate_base') {
    // Aggregate-base planner/execution signatures already encode the execution shape.
    // Keeping worksheet-scoped dimensions in the primary identity needlessly partitions
    // equivalent persisted nodes across workbook sheets, which defeats workbook-wide reuse.
    return {
      workingSetSignature: null,
      grainSignature: null,
      measureSetSignature: null,
      calcParamSignature: null,
      windowInputSignature: null,
    }
  }
  return {
    workingSetSignature: normalizeText(input.workingSetSignature),
    grainSignature: normalizeText(input.grainSignature),
    measureSetSignature: normalizeText(input.measureSetSignature),
    calcParamSignature: normalizeText(input.calcParamSignature),
    windowInputSignature: normalizeText(input.windowInputSignature),
  }
}

export const buildDatasourceExecutionScopeSignature = (value: any): string | null => {
  if (value === null || typeof value === 'undefined') return null
  const text = stableStringify(value)
  if (!text || text === 'null' || text === '[]' || text === '{}') return null
  return hashStringToBase36(text).slice(0, 24)
}

// Canonical datasource root key. One value per `(datasetId, extractIdentity, semanticSpecHash)`
// triple. Shaped nodes (aggregate_base, window_input_base, working_set) belonging to the same
// datasource should all share the same canonical root key so eviction, invalidation, and
// cross-sheet reuse can walk by root identity instead of reconstructing the triple each time.
export const buildCanonicalRootIdentityKey = (input: {
  datasetId?: string | null
  extractIdentity?: string | null
  semanticSpecHash?: string | null
}): string | null => {
  const datasetId = normalizeText(input.datasetId)
  if (!datasetId) return null
  return hashStringToBase36(
    stableStringify({
      nodeKind: 'semantic_base' as DatasourceExecutionNodeKind,
      datasetId,
      extractIdentity: normalizeText(input.extractIdentity),
      semanticSpecHash: normalizeText(input.semanticSpecHash),
    }),
  ).slice(0, 24)
}

export const buildDatasourceExecutionNodeIdentity = (
  input: DatasourceExecutionNodeIdentityInput,
): string | null => {
  const nodeKind = normalizeNodeKind(input.nodeKind)
  const plannerNodeSignature = normalizeText(input.plannerNodeSignature)
  if (!nodeKind || !plannerNodeSignature) return null
  const dimensions = normalizeNodeIdentityDimensions(nodeKind, input)
  return hashStringToBase36(
    stableStringify({
      nodeKind,
      datasetId: normalizeText(input.datasetId),
      route: normalizeText(input.route),
      extractIdentity: normalizeText(input.extractIdentity),
      semanticSpecHash: normalizeText(input.semanticSpecHash),
      workingSetSignature: dimensions.workingSetSignature,
      grainSignature: dimensions.grainSignature,
      measureSetSignature: dimensions.measureSetSignature,
      calcParamSignature: dimensions.calcParamSignature,
      windowInputSignature: dimensions.windowInputSignature,
      plannerNodeSignature,
      executionSignature: normalizeText(input.executionSignature),
    }),
  ).slice(0, 24)
}

export const buildDatasourceExecutionNodeTableName = (
  nodeKind: DatasourceExecutionNodeKind,
  identity: string | null | undefined,
): string | null => {
  const kind = normalizeNodeKind(nodeKind)
  const key = normalizeText(identity)
  if (!kind || !key) return null
  return `${tableNamePrefixByKind[kind]}${key.slice(0, 24)}`
}

export const normalizeDatasourceExecutionNodeManifestEntry = (
  entry: any,
): DatasourceExecutionNodeManifestEntry | null => {
  const nodeKind = normalizeNodeKind(entry?.node_kind ?? entry?.nodeKind)
  const key = normalizeText(entry?.key)
  const tableName = normalizeText(entry?.table_name ?? entry?.tableName)
  if (!nodeKind || !key || !tableName) return null
  const createdAtMs = Number(entry?.created_at_ms ?? entry?.createdAtMs ?? Date.now())
  const lastUsedAtMs = Number(entry?.last_used_at_ms ?? entry?.lastUsedAtMs ?? createdAtMs)
  const rowCount = Number(entry?.row_count ?? entry?.rowCount ?? NaN)
  const bytesEst = Number(entry?.bytes_est ?? entry?.bytesEst ?? NaN)
  const reuseCount = Number(entry?.reuse_count ?? entry?.reuseCount ?? NaN)
  return {
    key,
    node_kind: nodeKind,
    table_name: tableName,
    extract_identity: normalizeText(entry?.extract_identity ?? entry?.extractIdentity),
    semantic_spec_hash: normalizeText(entry?.semantic_spec_hash ?? entry?.semanticSpecHash),
    working_set_signature: normalizeText(entry?.working_set_signature ?? entry?.workingSetSignature),
    grain_signature: normalizeText(entry?.grain_signature ?? entry?.grainSignature),
    measure_set_signature: normalizeText(entry?.measure_set_signature ?? entry?.measureSetSignature),
    calc_param_signature: normalizeText(entry?.calc_param_signature ?? entry?.calcParamSignature),
    window_input_signature: normalizeText(entry?.window_input_signature ?? entry?.windowInputSignature),
    planner_node_signature: normalizeText(entry?.planner_node_signature ?? entry?.plannerNodeSignature),
    execution_signature: normalizeText(entry?.execution_signature ?? entry?.executionSignature),
    canonical_root_key: normalizeText(entry?.canonical_root_key ?? entry?.canonicalRootKey),
    created_at_ms: Number.isFinite(createdAtMs) ? Math.max(0, Math.floor(createdAtMs)) : Date.now(),
    last_used_at_ms: Number.isFinite(lastUsedAtMs) ? Math.max(0, Math.floor(lastUsedAtMs)) : Date.now(),
    row_count: Number.isFinite(rowCount) && rowCount >= 0 ? Math.floor(rowCount) : null,
    bytes_est: Number.isFinite(bytesEst) && bytesEst >= 0 ? Math.floor(bytesEst) : null,
    working_set_kind: normalizeText(entry?.working_set_kind ?? entry?.workingSetKind),
    reuse_count: Number.isFinite(reuseCount) && reuseCount >= 0 ? Math.floor(reuseCount) : null,
  }
}

export const sanitizeDatasourceExecutionNodeManifestEntries = (
  entriesRaw: any,
): DatasourceExecutionNodeManifestEntry[] => {
  if (!Array.isArray(entriesRaw)) return []
  const out: DatasourceExecutionNodeManifestEntry[] = []
  const seen = new Set<string>()
  for (const raw of entriesRaw) {
    const entry = normalizeDatasourceExecutionNodeManifestEntry(raw)
    if (!entry || seen.has(entry.key)) continue
    seen.add(entry.key)
    out.push(entry)
  }
  return out
}

// Phase 3a. Kinds that participate in the dataset-level shaped-cache byte budget.
// `semantic_base` is a logical identity with no physical table, so it is never
// evicted. Physical `working_set` tables are budgeted, but the planner tiers
// active/recent/reused/shared working sets behind colder shaped nodes.
export const DATASET_SHAPE_BUDGET_EVICTABLE_KINDS: ReadonlyArray<DatasourceExecutionNodeKind> = [
  'working_set',
  'aggregate_base',
  'window_input_base',
  'worksheet_presentation',
]

export const isDatasetShapeBudgetEvictableKind = (
  kind: DatasourceExecutionNodeKind | string | null | undefined,
): boolean => {
  if (!kind) return false
  return (DATASET_SHAPE_BUDGET_EVICTABLE_KINDS as ReadonlyArray<string>).includes(String(kind))
}

export type DatasetShapeBudgetPlan = {
  keep: DatasourceExecutionNodeManifestEntry[]
  evict: DatasourceExecutionNodeManifestEntry[]
  bytesRemaining: number
  bytesReclaimed: number
  totalBytesBefore: number
  budgetBytes: number
}

export type DatasetShapeBudgetPlanOptions = {
  nowMs?: number | null
  recentWorkingSetMs?: number | null
  workingSetReuseProtectThreshold?: number | null
  protectedWorkingSetSignatures?: string[] | null
}

const safeBytes = (entry: DatasourceExecutionNodeManifestEntry): number => {
  const b = Number(entry?.bytes_est ?? 0)
  return Number.isFinite(b) && b > 0 ? Math.floor(b) : 0
}

/**
 * Pure eviction planner for the Phase 3a dataset shaped-cache budget. Given the
 * current manifest entries for a datasetId and a `budgetBytes` ceiling, returns
 * which evictable entries must be removed to bring budgeted shaped-cache bytes
 * under the budget. Logical/protected kinds (`semantic_base`) are always kept
 * and do not consume the shaped-cache budget.
 *
 * Eviction order: lower-value cache tiers first, then LRU by `last_used_at_ms`
 * ascending. Ties are broken by largest `bytes_est` first so we reclaim the most
 * memory per eviction when timestamps collide.
 */
export const planDatasetShapeBudgetEviction = (
  entriesRaw: DatasourceExecutionNodeManifestEntry[] | any,
  budgetBytes: number,
  options?: DatasetShapeBudgetPlanOptions | null,
): DatasetShapeBudgetPlan => {
  const entries = Array.isArray(entriesRaw) ? entriesRaw : sanitizeDatasourceExecutionNodeManifestEntries(entriesRaw)
  const budget = Number.isFinite(budgetBytes) && budgetBytes > 0 ? Math.floor(budgetBytes) : 0
  const nowMsRaw = Number(options?.nowMs ?? Date.now())
  const nowMs = Number.isFinite(nowMsRaw) && nowMsRaw >= 0 ? Math.floor(nowMsRaw) : Date.now()
  const recentWorkingSetMsRaw = Number(options?.recentWorkingSetMs ?? 10 * 60_000)
  const recentWorkingSetMs = Number.isFinite(recentWorkingSetMsRaw) && recentWorkingSetMsRaw > 0
    ? Math.floor(recentWorkingSetMsRaw)
    : 0
  const reuseProtectThresholdRaw = Number(options?.workingSetReuseProtectThreshold ?? 2)
  const reuseProtectThreshold = Number.isFinite(reuseProtectThresholdRaw) && reuseProtectThresholdRaw > 0
    ? Math.floor(reuseProtectThresholdRaw)
    : Number.POSITIVE_INFINITY
  const protectedWorkingSetSignatures = new Set(
    (Array.isArray(options?.protectedWorkingSetSignatures) ? options!.protectedWorkingSetSignatures : [])
      .map((value) => normalizeText(value))
      .filter(Boolean),
  )
  const protectedEntries = entries.filter((entry) => !isDatasetShapeBudgetEvictableKind(entry?.node_kind))
  const evictable = entries.filter((entry) => isDatasetShapeBudgetEvictableKind(entry?.node_kind))
  const totalBytesBefore = evictable.reduce((sum, entry) => sum + safeBytes(entry), 0)
  if (totalBytesBefore <= budget) {
    return {
      keep: [...entries],
      evict: [],
      bytesRemaining: totalBytesBefore,
      bytesReclaimed: 0,
      totalBytesBefore,
      budgetBytes: budget,
    }
  }
  const evictionTier = (entry: DatasourceExecutionNodeManifestEntry): number => {
    const kind = String(entry?.node_kind ?? '')
    if (kind === 'worksheet_presentation') return 0
    if (kind === 'window_input_base') return 1
    if (kind === 'working_set') {
      const lastUsedAt = Number(entry?.last_used_at_ms ?? 0)
      const ageMs = Number.isFinite(lastUsedAt) ? Math.max(0, nowMs - Math.max(0, Math.floor(lastUsedAt))) : Number.POSITIVE_INFINITY
      const recent = recentWorkingSetMs > 0 && ageMs <= recentWorkingSetMs
      const reuseCount = Number(entry?.reuse_count ?? 0)
      const reused = Number.isFinite(reuseCount) && reuseCount >= reuseProtectThreshold
      const signature = normalizeText(entry?.working_set_signature)
      const explicitlyProtected = signature ? protectedWorkingSetSignatures.has(signature) : false
      return recent || reused || explicitlyProtected ? 4 : 2
    }
    if (kind === 'aggregate_base') return 3
    return 2
  }
  const sorted = [...evictable].sort((a, b) => {
    const tierDelta = evictionTier(a) - evictionTier(b)
    if (tierDelta !== 0) return tierDelta
    const lruDelta = Number(a.last_used_at_ms || 0) - Number(b.last_used_at_ms || 0)
    if (lruDelta !== 0) return lruDelta
    return safeBytes(b) - safeBytes(a)
  })
  const keepFromEvictable: DatasourceExecutionNodeManifestEntry[] = []
  const evict: DatasourceExecutionNodeManifestEntry[] = []
  let running = totalBytesBefore
  let bytesReclaimed = 0
  for (const victim of sorted) {
    if (running <= budget) {
      keepFromEvictable.push(victim)
      continue
    }
    const bytes = safeBytes(victim)
    // Zero-byte entries would not reduce `running` even if evicted, so keep them.
    // This avoids pointless churn for manifest rows that haven't been sized yet.
    if (bytes <= 0) {
      keepFromEvictable.push(victim)
      continue
    }
    evict.push(victim)
    running -= bytes
    bytesReclaimed += bytes
  }
  return {
    keep: [...protectedEntries, ...keepFromEvictable],
    evict,
    bytesRemaining: Math.max(0, running),
    bytesReclaimed,
    totalBytesBefore,
    budgetBytes: budget,
  }
}
